/**
 * PHOTOS §4, syncing: each picture goes up once, on its own, under the save code - never inside the save, which the
 * cloud layer re-sends whole on every change. The server only takes one for a save it ALREADY holds, so a pass
 * first gets the campaign up (flush and wait, as the wall's `settle` does) and sends nothing until a save has been
 * acknowledged. There is no second sync engine: a pass runs after a picture is kept, and after every save the cloud
 * layer has had acknowledged - the moments the server is known to hold this save.
 */

import type { CloudSave } from '../cloudSave/types';
import type { PhotoStore } from './store';
import type { PhotoTransport } from './transport';

/** What the photos need of the cloud layer: the code, and its rhythm. */
export type PhotoCloud = Pick<CloudSave, 'enabled' | 'code' | 'flush' | 'whenIdle' | 'status' | 'subscribe'>;

export interface PhotoSync {
  /** Send every picture the server does not hold yet. Never rejects; one pass at a time. */
  run(): Promise<void>;
  /** A picture was just kept: there is something to send. */
  kept(): void;
  /** Resolves when no pass is running: for the tests. */
  whenIdle(): Promise<void>;
}

/** 403 is "no save under this code yet": it mends itself once one is acknowledged. Other refusals do not. */
const NO_SAVE_YET = 403;

export function createPhotoSync(cloud: PhotoCloud, store: PhotoStore, transport: PhotoTransport): PhotoSync {
  /** Might a picture be waiting? True at the start: one taken offline in an earlier session still has to go. */
  let waiting = true;
  /** Counts pictures kept, so a pass that began before one was kept cannot declare nothing waiting. */
  let keptCount = 0;
  let running: Promise<void> | null = null;
  let again = false;
  /** Refused for good this session (a bad JPEG, a full disk): not sent again until the next start. */
  const given = new Set<string>();

  async function pass(): Promise<void> {
    if (!waiting || !cloud.enabled()) return;
    const keptBefore = keptCount;
    const pending = (await store.pending()).filter((r) => !given.has(r.id));
    // Nothing to send: the cloud layer keeps its own rhythm, undisturbed.
    if (pending.length === 0) {
      if (keptCount === keptBefore) waiting = false;
      return;
    }
    // The campaign first: a picture of a death the server has not heard of yet would be refused.
    cloud.flush();
    await cloud.whenIdle();
    const code = cloud.code();
    // No acknowledged exchange yet: there is no save under the code to file a picture with. The next one runs this.
    if (code === null || cloud.status().lastSyncedAt === null) return;
    let left = false;
    for (const record of pending) {
      const sent = await transport.put(code, record.id, record.jpeg);
      if (sent.kind === 'ok') await store.markUploaded(record.id);
      else if (sent.kind === 'refused' && sent.status !== NO_SAVE_YET) given.add(record.id);
      else left = true;
    }
    if (keptCount === keptBefore) waiting = left;
  }

  function run(): Promise<void> {
    if (running !== null) {
      again = true;
      return running;
    }
    // `running` is cleared in the same step as the last look at `again`: a request that arrives after that look
    // starts a pass of its own instead of being folded into one that has already finished. (The body always
    // awaits at least once, so the assignment below happens before the `finally`.)
    running = (async (): Promise<void> => {
      try {
        do {
          again = false;
          try {
            await pass();
          } catch {
            // Nothing in a pass is meant to throw; if something does, the picture simply waits for the next one.
          }
        } while (again);
      } finally {
        running = null;
      }
    })();
    return running;
  }

  return {
    run,
    kept() {
      waiting = true;
      keptCount += 1;
      void run();
    },
    async whenIdle() {
      while (running !== null) await running;
    },
  };
}

/**
 * A pass after every save the server ACKNOWLEDGED (`status().lastSyncedAt` moved), and one now for pictures an earlier
 * session could not send - the same rhythm as the wall's standings (`fileAfterEverySave` in `wall/index.ts`).
 */
export function syncAfterEverySave(cloud: PhotoCloud, sync: PhotoSync): void {
  let seen = cloud.status().lastSyncedAt;
  cloud.subscribe(() => {
    const at = cloud.status().lastSyncedAt;
    if (at === seen) return;
    seen = at;
    void sync.run();
  });
  void sync.run();
}
